import {
  Settings,
  Smartphone,
  CreditCard,
  Webhook,
  PanelTop,
  FlaskConical,
  ToggleRight,
  LucideProps,
} from "lucide-react";
import type { ForwardRefExoticComponent, RefAttributes } from "react";
import type { PlanFeatureFlag } from "@/lib/utils/planFeatures";

export type SidebarNavItem = {
  name: string;
  link: NAVIGATION_PATHS;
  icon?: ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
  >;
  disabled?: boolean;
  beta?: boolean;
  crossOrigin?: boolean;
  requiredEntitlement?: PlanFeatureFlag;
};

export enum NAVIGATION_PATHS {
  Signup = "/auth/signup",
  Signin = "/auth/signin",
  CreateOrganization = "/create-organization", // used also in protected group when creating new organization
  ConfirmEmail = "/auth/confirm-email",
  UpdatePassword = "/auth/update-password",
  ResetPassword = "/auth/reset-password",
  AuthCodeError = "/auth/auth-code-error",
  Mfa = "/auth/mfa",
  MfaSetup = "/auth/mfa/setup",
  AcceptInvite = "/auth/accept-invite",
  CliAuthorize = "/cli/authorize",
  AppUnavailable = "/applications/unavailable",
  TermsAndConditions = "/terms-and-conditions",
  PrivacyPolicy = "/privacy-policy",
  RefundTerms = "/refund-terms",
  CliLogin = "/cli-login", // main domain only

  // ------- dashboard
  Dashboard = "/",
  AnalyticsOverview = "/analytics/overview",
  AnalyticsLinks = "/analytics/links",
  AnalyticsEvents = "/analytics/app-events",
  AnalyticsImports = "/analytics/imports",
  AnalyticsSmartBanners = "/analytics/smart-banners",
  Applications = "/applications",
  Billing = "/billing",
  BillingPlans = "/billing/plans",
  Pricing = "/pricing",
  Webhooks = "/webhooks",
  SmartBanners = "/smart-banners",
  Experiments = "/experiments",
  FeatureFlags = "/feature-flags",

  // ------- settings
  OrganizationsSettings = "/settings/organizations",
  AccountSettings = "/settings/account", // used both under subdomain and main domain
}

export const NAVIGATION_HEADER_TITLES: Partial<
  Record<NAVIGATION_PATHS, string>
> = {
  [NAVIGATION_PATHS.Dashboard]: "Apps",
  [NAVIGATION_PATHS.AccountSettings]: "Account Settings",
  [NAVIGATION_PATHS.OrganizationsSettings]: "Organization Settings",
  [NAVIGATION_PATHS.Applications]: "Apps",
  [NAVIGATION_PATHS.AnalyticsOverview]: "Analytics",
  [NAVIGATION_PATHS.AnalyticsLinks]: "Analytics",
  [NAVIGATION_PATHS.AnalyticsEvents]: "Analytics",
  [NAVIGATION_PATHS.AnalyticsImports]: "Analytics",
  [NAVIGATION_PATHS.AnalyticsSmartBanners]: "Analytics",
  [NAVIGATION_PATHS.Billing]: "Billing",
  [NAVIGATION_PATHS.BillingPlans]: "Billing",
  [NAVIGATION_PATHS.Pricing]: "Pricing",
  [NAVIGATION_PATHS.SmartBanners]: "Smart banners",
  [NAVIGATION_PATHS.Experiments]: "Experiments",
  [NAVIGATION_PATHS.FeatureFlags]: "Feature flags",
};

export const SIDEBAR_APP_NAVIGATION: SidebarNavItem[] = [
  { name: "Apps", icon: Smartphone, link: NAVIGATION_PATHS.Dashboard },
];
export const SIDEBAR_ANALYTICS_NAVIGATION: SidebarNavItem[] = [
  { name: "Overview", link: NAVIGATION_PATHS.AnalyticsOverview },
  {
    name: "Links",
    link: NAVIGATION_PATHS.AnalyticsLinks,
    requiredEntitlement: "canAccessAnalyticsLinks",
  },
  {
    name: "Events",
    link: NAVIGATION_PATHS.AnalyticsEvents,
    requiredEntitlement: "canAccessAnalyticsEvents",
  },
  {
    name: "Smart banners",
    link: NAVIGATION_PATHS.AnalyticsSmartBanners,
    requiredEntitlement: "canUseSmartBanners",
  },
  { name: "Imports", link: NAVIGATION_PATHS.AnalyticsImports, beta: true },
];
export const SIDEBAR_EXPERIMENTS_NAVIGATION: SidebarNavItem[] = [
  {
    name: "Experiments",
    icon: FlaskConical,
    link: NAVIGATION_PATHS.Experiments,
    beta: true,
    requiredEntitlement: "canUseExperiments",
  },
  {
    name: "Feature flags",
    icon: ToggleRight,
    link: NAVIGATION_PATHS.FeatureFlags,
    beta: true,
    requiredEntitlement: "canUseExperiments",
  },
];
export const SIDEBAR_ORGANIZATION_NAVIGATION: SidebarNavItem[] = [
  {
    name: "Settings",
    icon: Settings,
    link: NAVIGATION_PATHS.OrganizationsSettings,
  },
  {
    name: "Billing",
    icon: CreditCard,
    link: NAVIGATION_PATHS.Billing,
    crossOrigin: true,
  },
  {
    name: "Smart banners",
    icon: PanelTop,
    link: NAVIGATION_PATHS.SmartBanners,
    beta: true,
  },
  {
    name: "Webhooks",
    icon: Webhook,
    link: NAVIGATION_PATHS.Webhooks,
  },
];
